import { DatasetCacheInfo, LocalDataset } from './local-dataset';
import { TILE_ATTRIBUTES } from './tile-attributes';

/**
 * Transport-agnostic core of the PLP dataset preload. Holds the paging loop and the query params
 * shared by the server fetcher. It deliberately imports NOTHING server-only (no `next/headers`) so it
 * stays safe to bundle into the client too. Each caller injects its own `callQuery` transport (which
 * always routes through the Redis-cached backend proxy — never a direct browser→Algolia call).
 */

const PAGE_SIZE = 1000; // Algolia max hitsPerPage
const MAX_PRELOAD_PAGES = 30; // safety stop (30k records)

/**
 * How many hits ride along in the SSR payload. The rest of the result set is withheld and pulled by
 * the browser in one top-up XHR right after hydration (see `LocalDatasetProvider`).
 *
 * This is deliberately the same number as one tile page, so the first paint contains exactly the
 * tiles the grid shows and not one record more. It is the single source of truth for both halves of
 * that relationship: `useTileLoading` re-exports it as TILE_PAGE_SIZE. It lives in this file because
 * this is the one module on the path that imports nothing server-only, so the server splitter and the
 * client hook can share it.
 *
 * NOTE: tiles beyond this batch are consequently NOT in the server-rendered HTML as full tiles. The
 * grid still renders every LOADED product, and the off-screen ones render a crawlable hook-free
 * <StaticProductTile> (a real <a href> with name, image and code), so internal linking survives.
 */
export const INITIAL_SSR_HITS = 12;

/**
 * Tile fields + the configured facet attributes, deduped (a facet may already be a tile field): the
 * request asks for exactly what the tile renders and the facet panel counts, nothing else.
 */
const retrieveAttributes = (facetAttributes: string[]): string[] =>
  Array.from(new Set([...TILE_ATTRIBUTES, ...facetAttributes]));

/** One entry of an Algolia multi-query: exactly the shape `client.search` takes. */
export interface AlgoliaRequestLog {
  indexName: string;
  params: Record<string, any>;
}

export interface AlgoliaQueryResponse {
  results: any[];
  cacheInfo?: Array<{ indexName: string; cached: boolean; nbHits: number }>;
  /**
   * What the backend actually handed to Algolia's `client.search` — the cache MISSES only. Empty when
   * every request was served from Redis. Requires the backend that echoes it (older deploys omit it).
   */
  algoliaRequests?: AlgoliaRequestLog[];
}

export interface CollectArgs {
  /** Products index (the AU/NZ "new first" base). */
  indexName: string;
  /** Algolia `filters` string — the CMS block's `algolia_filters_query`, `${localeKey}` resolved. */
  filters?: string;
  /**
   * The facet attribute keys, from the Contentstack facet configuration — the ONLY source of the
   * facet list on this path. They are passed straight through onto the dataset for the in-memory
   * engine to count; Algolia is never asked which facets exist.
   */
  facetAttributes?: string[];
  /** When true, bypass the Redis cache (fetch fresh from Algolia and re-store). Driven by `?clearCache`. */
  clearCache?: boolean;
}

/** Runs one or more index queries through the caller's transport and returns the raw Algolia responses. */
export type CallQuery = (
  requests: Array<{ indexName: string; params: any }>,
  clearCache: boolean,
) => Promise<AlgoliaQueryResponse>;

/**
 * A collected dataset plus the request log for the browser console. `requestLog` is folded into
 * `debug` by buildPlpDataset and is NOT part of LocalDataset, so nothing extra reaches the SSR
 * payload except the (small) debug block itself.
 */
export interface CollectedDataset extends LocalDataset {
  requestLog?: {
    /** Every multi-query this fetch POSTed to the `algoliaSearch/query` action (all pages). */
    request: AlgoliaRequestLog[];
    /** The subset the backend forwarded to Algolia's `client.search` — misses only; [] if all cached. */
    algoliaRequests: AlgoliaRequestLog[];
  };
}

// Listings are filters-only — no text query. `query: ''` stays explicit because the params object is the
// Redis cache key (backend AlgoliaApi), so its shape is part of the cache contract.
const baseParams = (filters: string, attributesToRetrieve: string[]) => ({
  query: '',
  filters,
  hitsPerPage: PAGE_SIZE,
  attributesToRetrieve,
  attributesToHighlight: [],
  attributesToSnippet: [],
  analytics: false,
});

/**
 * The page-0 request this fetch would send for the given inputs. Used to log the query on the FAILURE
 * path, where no dataset (and therefore no request log) comes back — which is exactly when knowing the
 * exact query matters most.
 */
export const plpRequestPreview = ({
  indexName,
  filters = '',
  facetAttributes = [],
}: CollectArgs): AlgoliaRequestLog[] => [
  { indexName, params: { ...baseParams(filters, retrieveAttributes(facetAttributes)), page: 0 } },
];

const aggregateCacheInfo = (infos: Array<{ cached: boolean }>): DatasetCacheInfo => {
  const cachedCount = infos.filter((i) => i?.cached).length;
  const algoliaCount = infos.length - cachedCount;
  const cached = infos.length > 0 && algoliaCount === 0;
  return { cached, cachedCount, algoliaCount, source: cached ? 'cache' : 'algolia' };
};

/**
 * Preload an entire PLP result set via `callQuery`. The first page doubles as the probe: its
 * `nbPages` yields how many more pages to pull.
 *
 * NOTHING facet-related is requested from Algolia on this path. The facet ATTRIBUTES come from the
 * Contentstack configuration (`facetAttributes`), and their VALUES, COUNTS and ORDER are all computed
 * in-memory from the hits (see local-dataset.ts `queryLocalDataset`) — the UI needs counts that react
 * to refinements, including disjunctive counts, which Algolia can only give with another round trip
 * per refinement. So every request here is a plain hits query: no `facets`, no `maxValuesPerFacet`,
 * and the response carries no `facets` / `facets_stats` / `renderingContent`. That keeps facet payload
 * out of the Algolia response, out of the Redis entry, and out of the SSR HTML, and leaves the facet
 * list/order owned solely by the CMS configuration.
 *
 * This is the server path only — /search (the browser path) still asks Algolia for facets and reads
 * them off the response.
 */
export const collectDataset = async (
  callQuery: CallQuery,
  { indexName, filters = '', facetAttributes = [], clearCache = false }: CollectArgs,
): Promise<CollectedDataset> => {
  const params = baseParams(filters, retrieveAttributes(facetAttributes));
  const cacheInfos: Array<{ cached: boolean }> = [];
  // Request log for the browser console (see section-with-product-listing): every request this fetch
  // POSTs, and what the backend forwarded from it to Algolia's `client.search`.
  const request: AlgoliaRequestLog[] = [];
  const algoliaRequests: AlgoliaRequestLog[] = [];
  const send = async (requests: AlgoliaRequestLog[]): Promise<AlgoliaQueryResponse> => {
    request.push(...requests);
    const response = await callQuery(requests, clearCache);
    algoliaRequests.push(...(response.algoliaRequests ?? []));
    cacheInfos.push(...(response.cacheInfo ?? []));
    return response;
  };

  const firstResponse = await send([{ indexName, params: { ...params, page: 0 } }]);
  const first = firstResponse.results[0] || {};
  const totalPages = Math.min(Number(first.nbPages) || 0, MAX_PRELOAD_PAGES);

  let hits: Record<string, any>[] = Array.isArray(first.hits) ? first.hits : [];

  if (totalPages > 1) {
    const rest = await send(
      Array.from({ length: totalPages - 1 }, (_, i) => ({ indexName, params: { ...params, page: i + 1 } })),
    );
    hits = hits.concat(rest.results.flatMap((r: any) => r.hits || []));
  }

  // The facet list IS the Contentstack configuration — there is no discovered-from-Algolia fallback.
  // A listing with no configured facets simply renders no facet panel, which is the correct outcome:
  // the raw index attribute names Algolia would report carry no type or label, so the facet
  // components (term/color/range/boolean) could not render them anyway.
  return {
    hits,
    facetNames: facetAttributes,
    cacheInfo: aggregateCacheInfo(cacheInfos),
    requestLog: { request, algoliaRequests },
  };
};
